import { areAllAssetPropertiesZero, getMaxYAndMinX } from 'apps/canvas/util/canvasUtil';
import { LocalStorageKeys } from 'core/constants/LocalStorageKeys';
import { getAspectRatio, getAspectRatioType } from 'core/utils/utils';
import { CanvasMediaItem } from '../../api/interface/CanvasMediaItem.ts';
import { FeedResponseItem } from '../../api/interface/FeedResponseItem.ts';
import { GenerateParamsData } from '../../api/interface/GenerateParams.ts';
import { GeneratedMediaResponse, GenerateResponse } from '../../api/interface/GenerateResponse.ts';
import { AspectRatio } from '../constants/enums/AspectRatioEnum.ts';
import { MediaFormatter } from './MediaFormatter.ts';

/* eslint-disable no-prototype-builtins */

/* eslint-disable class-methods-use-this */

export class CanvasMediaFormatter extends MediaFormatter {
	// @ts-ignore
	#latestGenerationX: number;

	// @ts-ignore
	#latestGenerationY: number;

	// @ts-ignore
	#currentY: number;

	constructor({ latestGenerationX = 0, latestGenerationY = 0 }) {
		super();
		this.#latestGenerationX = latestGenerationX;
		this.#latestGenerationY = latestGenerationY;
	}

	override getMediaDimensions = (
		mediaResponse: GeneratedMediaResponse,
		isFirstLoad: boolean = true,
	) => {
		const { height, width } = mediaResponse.dim ?? mediaResponse?.metadata?.dim ?? {};
		const { properties } = mediaResponse;
		const aspectRatio = height !== 0 ? getAspectRatio(width / height) : 1;
		const aspectRatioType = height !== 0 ? getAspectRatioType(width / height) : AspectRatio.SQUARE;
		if (isFirstLoad || !properties?.scale?.height || !properties?.scale?.width) {
			return {
				height: height * 0.2,
				width: width * 0.2,
				originalHeight: height,
				originalWidth: width,
				aspectRatio,
				aspectRatioType,
			};
		}
		return {
			height: properties.scale.height,
			width: properties.scale.width,
			originalHeight: height,
			originalWidth: width,
			aspectRatio,
			aspectRatioType,
		};
	};

	override getMediaPlacementDetails = (
		mediaResponse: GeneratedMediaResponse,
		mediaIndex?: number,
		isFirstLoad: boolean = true,
	) => {
		const { properties } = mediaResponse;
		const mediaDimensions = this.getMediaDimensions(mediaResponse, isFirstLoad);
		let x = properties?.position?.x;
		let y = properties?.position?.y;
		if (!properties?.scale?.width || !properties?.scale?.height) {
			x = this.#latestGenerationX + mediaIndex * (mediaDimensions.width + 10);
			y = this.#latestGenerationY;
		} else if (isFirstLoad) {
			x = 20 + mediaIndex * (mediaDimensions.width + 10);
			y = this.#currentY;
			this.#currentY += mediaDimensions.height + 30;
		}
		return {
			...mediaDimensions,
			x,
			y,
		};
	};

	override getMiscGenerationDetails = (mediaResponse: GeneratedMediaResponse) => {
		const { status, properties, seed } = mediaResponse;
		return {
			loading: status === 'pending' || status === 'initiated',
			newImage: !properties?.scale?.height || !properties?.scale?.width,
			status,
			seed,
		};
	};

	getCanvasFormattedGeneratedItems = (
		generateResponse: GenerateResponse,
		generateParams: GenerateParamsData,
	) => {
		const formattedAssets: Record<string, CanvasMediaItem> = {};
		const formattedAssetProperties = [];
		const { media: mediaList } = generateResponse;
		const isFirstLoad = true;
		mediaList.forEach((mediaResponse, mediaIndex) => {
			const {
				media_id: assetId,
				media_type: assetType,
				generation_type: generationType,
				scenes,
				state,
			} = mediaResponse;
			const {
				isTrueTouch,
				isChangeIt,
				isUpscale,
				isFaceReplace,
				isImageSettingsHidden,
				isClothSwap,
			} = this.getImageToImageSettings(generateResponse, mediaResponse);
			const mediaPlacementDetails = this.getMediaPlacementDetails(
				mediaResponse,
				mediaIndex,
				isFirstLoad,
			);
			const mediaCheckpointSettings = this.getMediaCheckpointSettings(
				mediaResponse,
				generateParams,
			);
			formattedAssets[assetId] = {
				id: assetId,
				assetType,
				scenes,
				state,
				src: '',
				skLoader: false,
				thumbnail: '',
				...mediaPlacementDetails,
				...this.getMiscGenerationDetails(mediaResponse),
				canvasSettings: {
					id: assetId,
					image: '',
					...(isImageSettingsHidden
						? {}
						: {
								...this.getPromptDetails(generateResponse, mediaResponse, generateParams),
								...mediaCheckpointSettings,
								aspectRatioType: mediaPlacementDetails.aspectRatioType,
							}),
					isTrueTouch,
					isChangeIt,
					isUpscale,
					isFaceReplace,
					isClothSwap,
					generationType,
					attributes: isImageSettingsHidden
						? undefined
						: {
								controlNet: this.getControlNetSettings(generateParams?.controlNetOptions),
								facelock: this.getFacelockSettings(generateParams?.facelockOptions, mediaResponse),
								loras: generateParams?.selectedLorasMap,
								faceSwap: this.getFaceSwapSettings(generateParams?.faceSwapOptions, mediaResponse),
								lipSync: this.getLipSyncSettings(generateParams?.lipSyncOptions, mediaResponse),
								character: this.getCharacterDetails(mediaResponse, generateParams),
								videoAnyone: this.getVideoAnyoneSettings(
									generateParams?.videoAnyoneOptions,
									mediaResponse,
								),
								imageReference: {
									fluxImageToImage: this.getImageReferenceStyleSettings(
										generateParams?.imageReferenceOptions?.fluxImageToImage,
										mediaResponse,
									),
								},
							},
				},
			};
			const imageProperty = {
				image_id: assetId,
				position: { x: mediaPlacementDetails.x, y: mediaPlacementDetails.y },
				scale: { height: mediaPlacementDetails.height, width: mediaPlacementDetails.width },
			};
			formattedAssetProperties.push(imageProperty);
		});
		const { maxY } = getMaxYAndMinX(formattedAssetProperties);
		localStorage.setItem(
			LocalStorageKeys.MAX_Y_COORDINATE,
			String(Math.max(maxY, this.#latestGenerationY)),
		);

		return {
			formattedAssets,
			formattedAssetProperties,
		};
	};

	getCanvasFormattedFeedItems = (feedItems: FeedResponseItem[]) => {
		const formattedAssets: Record<string, CanvasMediaItem> = {};
		const formattedAssetProperties = [];
		this.#currentY = 20;
		const isFirstLoad = areAllAssetPropertiesZero(feedItems);
		feedItems.forEach((feedItem) => {
			const { media: mediaList } = feedItem;
			mediaList.forEach((mediaResponse, mediaIndex) => {
				const {
					isTrueTouch,
					isChangeIt,
					isUpscale,
					isFaceReplace,
					isClothSwap,
					isImageSettingsHidden,
					state: musicVideoStates,
					audio: audioDetails,
				} = this.getImageToImageSettings(feedItem, mediaResponse);
				const {
					media_id: assetId,
					media_type: assetType,
					output: imageSrc,
					thumbnail,
				} = mediaResponse;
				const mediaPlacementDetails = this.getMediaPlacementDetails(
					mediaResponse,
					mediaIndex,
					isFirstLoad,
				);
				formattedAssets[assetId] = {
					id: assetId,
					assetType,
					musicVideoStates,
					audioDetails,
					src: imageSrc,
					skLoader: false,
					thumbnail,
					...mediaPlacementDetails,
					...this.getMiscGenerationDetails(mediaResponse),
					canvasSettings: {
						id: assetId,
						image: imageSrc,
						...(isImageSettingsHidden
							? {}
							: {
									...this.getPromptDetails(feedItem, mediaResponse),
									...this.getMediaCheckpointSettings(mediaResponse),
									aspectRatioType: mediaPlacementDetails.aspectRatioType,
								}),
						isTrueTouch,
						isChangeIt,
						isClothSwap,
						isUpscale,
						isFaceReplace,
						...this.getGenerationState(feedItem),
						attributes: isImageSettingsHidden
							? {}
							: {
									controlNet: this.getControlNetSettings(feedItem.params.attributes.control_net),
									facelock: this.getFacelockSettings(
										feedItem.params.attributes.facelock,
										mediaResponse,
									),
									loras: feedItem.params.attributes.loras,
									faceSwap: this.getFaceSwapSettings(
										feedItem.params.attributes.face_swap,
										mediaResponse,
									),
									lipSync: this.getLipSyncSettings(
										feedItem.params.attributes.lip_sync,
										mediaResponse,
									),
									character: this.getCharacterDetails(mediaResponse),
									videoAnyone: this.getVideoAnyoneSettings(
										feedItem.params.attributes.video_anyone,
										mediaResponse,
									),
									imageReference: {
										fluxImageToImage: this.getImageReferenceStyleSettings(
											feedItem.params.attributes.flux_image_to_image,
											mediaResponse,
										),
									},
								},
					},
				};

				const imageProperty = {
					image_id: assetId,
					position: { x: mediaPlacementDetails.x, y: mediaPlacementDetails.y },
					scale: { height: mediaPlacementDetails.height, width: mediaPlacementDetails.width },
				};
				formattedAssetProperties.push(imageProperty);
			});
		});
		if (formattedAssetProperties.length !== 0) {
			localStorage.setItem('maxYCoordinate', String(getMaxYAndMinX(formattedAssetProperties).maxY));
		} else if (formattedAssetProperties.length === 0 && isFirstLoad) {
			localStorage.setItem('maxYCoordinate', '30');
		}
		return {
			formattedAssets,
			formattedAssetProperties,
			isFirstLoad,
		};
	};
}

/* eslint-disable class-methods-use-this */
/* eslint-disable no-prototype-builtins */
